import { useState, useEffect } from "react";
import { Row, Col, OverlayTrigger, Tooltip, Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import api from "../../services/api";
import MostrarNota from "./MostrarNota";
import { useAuth } from "../../context/auth";

const normalizar = (texto) =>
  (texto || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // tira acentos
    .toLowerCase()
    .trim();

  const CAMPOS_UERJ = [
  "adequacao_tema",
  "tipo_texto",
  "argumentacao",
  "periodo_coesao",
  "modalidade",
];

const calcularNotaUerj = (notaUerj) => {
  if (!notaUerj) return 0;

  if (notaUerj.nota_final != null) {
    return Number(notaUerj.nota_final) || 0;
  }
  if (notaUerj.nota_total != null) {
    return Number(notaUerj.nota_total) || 0;
  }

  const soma = CAMPOS_UERJ.reduce((total, campo) => {
    const valor = Number(notaUerj[campo]) || 0;
    return total + valor;
  }, 0);

  return Math.round(soma);
};

const ListaAlunos = ({ turma, id_turma }) => {
  const [loading, setLoading] = useState(true);

  /*
    Remover aluno da turma é destrutivo e o botão fica ao lado do nome — um clique
    errado tirava o aluno na hora, sem volta. Passa por confirmação.
  */
  const [alunoParaRemover, setAlunoParaRemover] = useState(null);
  const [removendo, setRemovendo] = useState(false);

  const [atividades, setAtividades] = useState([]);

  const [alunosTurma, setAlunosTurma] = useState([]);

  const [temas, setTemas] = useState([]);
  const [idTema, setIdTema] = useState(0);
  const [showDropdownTema, setShowDropdownTema] = useState(false);
  const [showDropdownAluno, setShowDropdownAluno] = useState(false);

  const [offset, setOffset] = useState(1);
  const [paginas, setPaginas] = useState(1);
  
  // Paginação para alunos
  const [alunosOffset, setAlunosOffset] = useState(1);
  const [alunosPorPagina] = useState(5); // Mostrar apenas 5 alunos por vez
  const [totalAlunos, setTotalAlunos] = useState(0);
  const [alunosPaginas, setAlunosPaginas] = useState(1);

  // Dados completos da turma para exportação - (rota /alunos/:turma_id)
  const [dadosTurmaCompleto, setDadosTurmaCompleto] = useState([]);

  // detectar tipo de turma. Se no nome tiver "UERJ", tratamos como UERJ
  const tipoTurma =
    typeof turma === "string" && turma.toUpperCase().includes("UERJ")
      ? "UERJ"
      : "ENEM";
  
  // Busca e filtros
  const [buscaAluno, setBuscaAluno] = useState("");
  const [buscaTema, setBuscaTema] = useState("");
  const [todosAlunosOriginal, setTodosAlunosOriginal] = useState([]);

  // Estados para adicionar aluno
  const [alunos, setAlunos] = useState([]);
  const [id_aluno, setIdAluno] = useState();
  const [buscaAlunoAdicionar, setBuscaAlunoAdicionar] = useState("");
  const [showDropdownAlunoAdicionar, setShowDropdownAlunoAdicionar] = useState(false);

  const { user } = useAuth();

  const getAlunos = async () => {
    try {
      let response = await api.get("/usuarios");

      setAlunos(response.data);
    } catch (err) {
    } finally {
      return false;
    }
  };

  const handleAddAluno = async () => {
    const data = {
      id_turma,
      id_aluno,
    };

    try {
      await api.post("/addAlunoTurma", data);

      toast.success("Aluno adicionado com sucesso!");
      
      // Limpar campos após sucesso
      setBuscaAlunoAdicionar("");
      setIdAluno(null);
      setShowDropdownAlunoAdicionar(false);
      
      setLoading(true);
      loadAlunos();
    } catch (err) {
      toast.error("Erro ao adicionar aluno!");
    }
  };

  const loadTemas = async () => {
    // Limpar atividades antes de carregar novas para evitar acúmulo
    setAtividades([]);
    setLoading(true);
    
    const data = {
      id: id_turma,
      tipo: "Todos",
      offset,
      temaId: idTema,
    };

    try {
      const response = await api.post(`/getAtividades`, data);
      setAtividades(response.data.atividades);
      setPaginas(response.data.paginas);
      setLoading(false);
    } catch (error) {
      toast.error("Erro as atividades da turma");
      setLoading(false);
    }
  };

  const confirmarRemocao = async () => {
    if (!alunoParaRemover) return;

    setRemovendo(true);

    try {
      await api.delete(`/deleteAlunoTurma/${id_turma}/${alunoParaRemover.id}`);

      toast.success(`${alunoParaRemover.name} foi removido(a) da turma.`);
      setAlunoParaRemover(null);
      setLoading(true);
      loadAlunos();
    } catch (err) {
      toast.error("Erro ao remover o aluno da turma.");
    } finally {
      setRemovendo(false);
    }
  };

  const getTemas = async () => {
    setLoading(true);
    try {
      // Usar o mesmo endpoint da página de atividades
      let response = await api.get(`/mostrar-temas/${id_turma}`);
      
      // Filtrar apenas os temas que estão "Em Uso" na turma (existentes)
      const temasUnicos = [];
      const temasVistos = new Set();
      
      if (response.data.temas && response.data.existentes) {
        // Criar um Set com os IDs dos temas existentes/em uso
        const temasExistentesIds = new Set(response.data.existentes.map(existente => existente.id));
        
        response.data.temas.forEach(tema => {
          // Só incluir temas que estão na lista de existentes (em uso)
          if (tema.id && temasExistentesIds.has(tema.id) && !temasVistos.has(tema.id)) {
            temasVistos.add(tema.id);
            temasUnicos.push({
              id: tema.id,
              nome: tema.nome
            });
          }
        });
      }
      
      setTemas(temasUnicos);
      console.log("Temas em uso carregados:", temasUnicos);
    } catch (err) {
      console.error("Erro ao carregar temas da turma:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAlunos = async () => {
    try {
      const response = await api.get(`/pegarAlunosPorTurma/${id_turma}`);
      const todosAlunos = response.data;
      
      setTodosAlunosOriginal(todosAlunos);
      aplicarFiltrosEPaginacao(todosAlunos);
      setLoading(false);
    } catch (err) {
      toast.error("Erro ao carregar alunos, tente novamente mais tarde!");
    }
  };

  // carrega os dados completos da turma (usa o helper novo do back)
  const carregarDadosTurmaCompleto = async () => {
    // Só precisa para UERJ; para outras turmas pode ignorar
    if (tipoTurma !== "UERJ") return;

    try {
      const response = await api.get(`/alunos/${id_turma}`);
      setDadosTurmaCompleto(response.data.data || []);
    } catch (error) {
      console.error("Erro ao carregar dados completos da turma:", error);
    }
  };

  // dadosTurmaCompleto vem da rota /alunos/:id_turma
  // tipoTurma já tem sido definido lá em cima

const getNotaPreCarregada = (nomeAluno, nomeTema) => {
  console.log("DEBUG aluno front:", nomeAluno);
  console.log("DEBUG temas front:", nomeTema);
  console.log("DEBUG dadosTurmaCompleto:", dadosTurmaCompleto);

  if (tipoTurma !== "UERJ") return null;
  if (!dadosTurmaCompleto || dadosTurmaCompleto.length === 0) return null;

  const alvoAluno = normalizar(nomeAluno);
  const alvoTema = normalizar(nomeTema);

  // encontrar o registro do aluno
  const registroAluno = dadosTurmaCompleto.find((item) => {
    const candidato =
      normalizar(item.aluno) ||
      normalizar(item.nome_aluno) ||
      normalizar(item.nome);

    return (
      candidato === alvoAluno ||
      candidato.includes(alvoAluno) ||
      alvoAluno.includes(candidato)
    );
  });

  if (
    !registroAluno ||
    !registroAluno.redacoes ||
    registroAluno.redacoes === "Inexistente" ||
    !Array.isArray(registroAluno.redacoes)
  ) {
    return null;
  }

  // encontrar a redação do tema
  const redacaoTema = registroAluno.redacoes.find((r) => {
    const candidatoTema =
      normalizar(r.tema) ||
      normalizar(r.nome_tema) ||
      normalizar(r.titulo);

    return (
      candidatoTema === alvoTema ||
      candidatoTema.includes(alvoTema) ||
      alvoTema.includes(candidatoTema)
    );
  });

  return redacaoTema && redacaoTema.notas ? redacaoTema.notas : null;
};


  
  const aplicarFiltrosEPaginacao = (alunos) => {
    // Aplicar filtro de busca
    let alunosFiltrados = alunos;
    if (buscaAluno.trim()) {
      alunosFiltrados = alunos.filter(aluno => 
        aluno.name.toLowerCase().includes(buscaAluno.toLowerCase())
      );
    }
    
    // Implementar paginação
    setTotalAlunos(alunosFiltrados.length);
    setAlunosPaginas(Math.ceil(alunosFiltrados.length / alunosPorPagina));
    
    const startIndex = (alunosOffset - 1) * alunosPorPagina;
    const endIndex = startIndex + alunosPorPagina;
    const alunosPaginados = alunosFiltrados.slice(startIndex, endIndex);
    
    setAlunosTurma(alunosPaginados);
  };

  // Função para exportar dados para Excel
  const exportarParaExcel = async () => {
    const toastId = toast.loading("Preparando dados para exportação...");
    
    try {
      // Usar o novo endpoint que retorna dados estruturados
      toast.update(toastId, { render: "Carregando dados da turma...", type: "info", isLoading: true });
      const response = await api.get(`/alunos/${id_turma}`);
      const dadosTurma = response.data.data;
      
      if (!dadosTurma || dadosTurma.length === 0) {
        toast.update(toastId, { render: "Nenhum aluno encontrado para exportar.", type: "warning", isLoading: false, autoClose: 3000 });
        return;
      }
      
      toast.update(toastId, { render: "Processando dados para Excel...", type: "info", isLoading: true });
      
      // Usar todos os temas da turma (incluindo aqueles sem redações)
      // Primeiro, coletar temas das redações existentes
      const temasComRedacoes = new Set();
      dadosTurma.forEach(aluno => {
        if (aluno.redacoes !== "Inexistente" && Array.isArray(aluno.redacoes)) {
          aluno.redacoes.forEach(redacao => {
            temasComRedacoes.add(redacao.tema);
          });
        }
      });
      
      // Combinar com todos os temas da turma (carregados na página)
      const todosOsTemas = new Set();
      
      // Adicionar temas que já estão carregados na página
      temas.forEach(tema => {
        todosOsTemas.add(tema.nome);
      });
      
      // Adicionar também os temas que aparecem nas redações (caso algum não esteja na lista de temas)
      temasComRedacoes.forEach(tema => {
        todosOsTemas.add(tema);
      });
      
      const temasArray = Array.from(todosOsTemas).sort();
      
      // Preparar dados para o Excel
      const dadosExcel = [];
      
      dadosTurma.forEach(aluno => {
         const linhaExcel = {
           'Nome do Aluno': aluno.aluno
         };
         
         // Se o endpoint retorna "Inexistente", significa que o aluno não fez nenhuma redação
         if (aluno.redacoes === "Inexistente") {
           // Marcar todas as colunas como "Inexistente"
           temasArray.forEach(tema => {
             linhaExcel[tema] = "Não fez";
           });
         } else if (Array.isArray(aluno.redacoes) && aluno.redacoes.length > 0) {
           // O aluno fez pelo menos uma redação
           // Inicializar todas as colunas com "Não fez" (assumindo que não fez todos os temas)
           temasArray.forEach(tema => {
             linhaExcel[tema] = "Não fez";
           });

           const turmaEhUERJ = tipoTurma === "UERJ";
           
           // Preencher apenas os temas que o aluno realmente fez
           aluno.redacoes.forEach((redacao) => {
            if (redacao.notas) {
              if (turmaEhUERJ) {
                // UERJ
                linhaExcel[redacao.tema] = calcularNotaUerj(redacao.notas);
              } else {
                // ENEM
                linhaExcel[redacao.tema] = redacao.notas.nota_final || "";
              }
            } else if (redacao.tema) {
              linhaExcel[redacao.tema] = "Aguardando correção";
            }
          });
         } else {
           // Caso inesperado: não é "Inexistente" mas também não é array válido
           temasArray.forEach(tema => {
             linhaExcel[tema] = "Erro nos dados";
           });
         }
         
         dadosExcel.push(linhaExcel);
       });
      
      // Se não há dados para exportar
      if (dadosExcel.length === 0) {
        toast.update(toastId, { render: "Nenhum dado encontrado para exportar.", type: "warning", isLoading: false, autoClose: 3000 });
        return;
      }
      
      // Criar workbook e worksheet
      toast.update(toastId, { render: "Gerando arquivo Excel...", type: "info", isLoading: true });
      
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(dadosExcel);
      
      // Definir largura das colunas dinamicamente
      const columnWidths = [
        { wch: 25 } // Nome do Aluno
      ];
      
      // Adicionar largura para cada tema (coluna)
      temasArray.forEach(() => {
        columnWidths.push({ wch: 15 }); // Largura para cada tema
      });
      
      worksheet['!cols'] = columnWidths;
      
      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Alunos e Atividades');
      
      // Gerar nome do arquivo com data atual
      const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const nomeArquivo = `Alunos_${turma}_${dataAtual}.xlsx`;
      
      // Fazer download do arquivo
      XLSX.writeFile(workbook, nomeArquivo);
      
      toast.update(toastId, { 
        render: `Dados exportados com sucesso! Arquivo: ${nomeArquivo}`, 
        type: "success", 
        isLoading: false, 
        autoClose: 5000 
      });
      
    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      toast.update(toastId, { 
        render: 'Erro ao exportar dados para Excel. Tente novamente.', 
        type: "error", 
        isLoading: false, 
        autoClose: 5000 
      });
    }
  };

  useEffect(() => {
    console.log("idTema ", idTema);
    loadTemas();
    getAlunos();
  }, [idTema, offset]);
  
  useEffect(() => {
    if (todosAlunosOriginal.length > 0) {
      aplicarFiltrosEPaginacao(todosAlunosOriginal);
    }
  }, [alunosOffset, alunosPorPagina, buscaAluno]);
  
  // Reset da paginação quando busca muda
  useEffect(() => {
    setAlunosOffset(1);
  }, [buscaAluno]);

  useEffect(() => {
    loadAlunos();
    getTemas();
    carregarDadosTurmaCompleto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtrar alunos para adicionar baseado na busca
  const alunosFiltradosAdicionar = alunos.filter(aluno =>
    aluno.name.toLowerCase().includes(buscaAlunoAdicionar.toLowerCase())
  );

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Verificar dropdown de aluno (filtros)
      const alunoContainer = event.target.closest('.dropdown-aluno-container');
      if (!alunoContainer) {
        setShowDropdownAluno(false);
      }
      
      // Verificar dropdown de tema
      const temaContainer = event.target.closest('.dropdown-tema-container');
      if (!temaContainer) {
        setShowDropdownTema(false);
      }
      
      // Verificar dropdown de adicionar aluno
      const adicionarAlunoContainer = event.target.closest('.dropdown-adicionar-aluno-container');
      if (!adicionarAlunoContainer) {
        setShowDropdownAlunoAdicionar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <Row>
        <Col>
          <div className="shadow-sm p-3 mb-4 bg-white rounded fdark">
            <strong>Exibindo Alunos: {turma}</strong>
          </div>
        </Col>
      </Row>

      {user.nivel === "a" && (
        <Row className="mb-4">
          <Col sm={12}>
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light border-0">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0 text-dark font-weight-bold">
                    <i className="fas fa-user-plus text-primary mr-2"></i>
                    Adicionar Aluno à Turma
                  </h5>
                  <div className="text-center">
                    <small className="text-muted font-weight-bold">
                      Selecione um aluno cadastrado
                    </small>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <Row>
                  <Col xs={12} md={8} className="mb-3">
                    {alunos && alunos.length >= 1 && (
                      <div className="form-group">
                        <label className="font-weight-bold text-dark">
                          <i className="fas fa-user text-primary mr-1"></i>
                          Escolher Aluno
                        </label>
                        <div className="dropdown-adicionar-aluno-container position-relative">
                          <div className="input-group">
                            <div className="input-group-prepend">
                              <span className="input-group-text">
                                <i className="fas fa-graduation-cap"></i>
                              </span>
                            </div>
                            <input
                              type="text"
                              className="form-control form-control-lg"
                              placeholder="Digite o nome do aluno ou clique para ver a lista"
                              value={buscaAlunoAdicionar}
                              onChange={(e) => {
                                setBuscaAlunoAdicionar(e.target.value);
                                setShowDropdownAlunoAdicionar(true);
                              }}
                              onFocus={() => setShowDropdownAlunoAdicionar(true)}
                            />
                            <div className="input-group-append">
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => {
                                  setBuscaAlunoAdicionar("");
                                  setIdAluno(null);
                                  setShowDropdownAlunoAdicionar(false);
                                }}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          </div>
                          {showDropdownAlunoAdicionar && (
                            <div className="dropdown-menu show w-100 mt-1" style={{maxHeight: '200px', overflowY: 'auto'}}>
                              {alunosFiltradosAdicionar.length > 0 ? (
                                alunosFiltradosAdicionar.map((aluno, index) => (
                                  <button
                                    key={index}
                                    className="dropdown-item d-flex align-items-center"
                                    onClick={() => {
                                      setBuscaAlunoAdicionar(aluno.name);
                                      setIdAluno(aluno.id);
                                      setShowDropdownAlunoAdicionar(false);
                                    }}
                                  >
                                    <i className="fas fa-user text-primary mr-2"></i>
                                    {aluno.name}
                                  </button>
                                ))
                              ) : (
                                <div className="dropdown-item-text text-muted">
                                  <i className="fas fa-search mr-2"></i>
                                  Nenhum aluno encontrado
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Col>
                  <Col xs={12} md={4} className="mb-3">
                    <div className="form-group">
                      <label className="font-weight-bold text-dark">
                        <i className="fas fa-cog text-primary mr-1"></i>
                        Ação
                      </label>
                      <button 
                        className="btn btn-success btn-lg btn-block" 
                        onClick={() => handleAddAluno()}
                        disabled={!alunos || alunos.length === 0 || !id_aluno}
                      >
                        <i className="fas fa-plus mr-2"></i>
                        Adicionar Aluno
                      </button>
                    </div>
                  </Col>
                </Row>
                {(!alunos || alunos.length === 0) && (
                  <div className="alert alert-warning mb-0">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Atenção:</strong> Nenhum aluno cadastrado encontrado. Cadastre alunos primeiro para poder adicioná-los à turma.
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Row>
        {loading ? (
          <>
            <Col className="text-center">
              {/*
                Um indicador só. Antes havia três (spinner, ícone girando no título e
                barra de progresso), e o "Loading..." usava `visually-hidden`, classe
                do Bootstrap 5 — o projeto é 4.6, então o texto não era escondido e
                girava junto com o spinner. Aqui o rótulo é acessível por aria-label.
              */}
              <div className="shadow p-5 mb-4 bg-white rounded">
                <div className="d-flex flex-column align-items-center">
                  <div
                    role="status"
                    aria-label="Carregando dados da turma"
                    style={{
                      width: '38px',
                      height: '38px',
                      border: '3px solid #e8eef5',
                      borderTopColor: '#009bd9',
                      borderRadius: '50%',
                      animation: 'girarLista 0.8s linear infinite',
                      marginBottom: '18px',
                    }}
                  />
                  <style>{`@keyframes girarLista { to { transform: rotate(360deg); } }`}</style>
                  <h5 style={{ color: '#024995', fontWeight: 700, margin: '0 0 6px' }}>
                    Carregando dados da turma
                  </h5>
                  <p className="mb-0" style={{ color: '#8698ab', fontSize: '14px' }}>
                    Aguarde enquanto buscamos as informações dos alunos e atividades...
                  </p>
                </div>
              </div>
            </Col>
          </>
        ) : (
          <>
            <Col>
              <div className="shadow p-3 mb-4 bg-white rounded fdark" style={{minHeight: '90vh'}}>
                <div className="table-responsive" style={{minHeight: '85vh'}}>

                {/* Controles de Paginação de Alunos */}
                  {totalAlunos > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
                       <div>
                         <strong className="text-dark">Página {alunosOffset} de {alunosPaginas}</strong>
                         <small className="text-dark d-block">Mostrando {alunosPorPagina} alunos por página</small>
                       </div>
                      <div>
                        <button
                           className="btn btn-outline-dark btn-sm mr-2"
                           onClick={() => setAlunosOffset(alunosOffset <= 1 ? 1 : alunosOffset - 1)}
                           disabled={alunosOffset <= 1}
                         >
                           ← Anterior
                         </button>
                         <span className="mx-2 text-dark">{alunosOffset}</span>
                         <button
                           className="btn btn-outline-dark btn-sm"
                           onClick={() => setAlunosOffset(alunosOffset >= alunosPaginas ? alunosPaginas : alunosOffset + 1)}
                           disabled={alunosOffset >= alunosPaginas}
                         >
                           Próxima →
                         </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Seção de Filtros Melhorada */}
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-header bg-light border-0">
                      <div className="d-flex align-items-center justify-content-between">
                        <h5 className="mb-0 text-dark font-weight-bold">
                          <i className="fas fa-filter text-primary mr-2"></i>
                          Filtros de Busca
                        </h5>
                        <div className="d-flex gap-2">
                          <button 
                            className="btn btn-success btn-sm"
                            onClick={exportarParaExcel}
                            title="Exportar todos os dados para Excel"
                            style={{marginRight: '10px'}}
                          >
                            <i className="fas fa-file-excel mr-1"></i>
                            Exportar em planilha Excel
                          </button>
                          <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                               setBuscaAluno("");
                               setBuscaTema("");
                               setIdTema(0);
                               setShowDropdownAluno(false);
                               setShowDropdownTema(false);
                             }}
                          >
                            <i className="fas fa-times mr-1"></i>
                            Limpar Filtros
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="card-body" style={{ overflow: 'visible' }}>
                      <div className="row">
                        {/* Busca por Nome */}
                         <div className="col-xs-12 col-md-6 mb-3">
                           <div className="form-group dropdown-aluno-container" style={{ position: 'relative' }}>
                             <label className="font-weight-bold text-dark">
                               <i className="fas fa-search text-primary mr-1"></i>
                               Buscar por Nome
                             </label>
                             <div className="input-group">
                               <div className="input-group-prepend">
                                 <span className="input-group-text">
                                   <i className="fas fa-user"></i>
                                 </span>
                               </div>
                               <input
                                 type="text"
                                 className="form-control form-control-lg"
                                 placeholder="Digite o nome do aluno..."
                                 value={buscaAluno}
                                 onChange={(e) => setBuscaAluno(e.target.value)}
                                 onFocus={() => setShowDropdownAluno(true)}
                               />
                               {buscaAluno && (
                                 <div className="input-group-append">
                                   <button
                                     className="btn btn-outline-secondary"
                                     type="button"
                                     onClick={() => {
                                       setBuscaAluno("");
                                       setShowDropdownAluno(false);
                                     }}
                                     title="Limpar busca"
                                   >
                                     <i className="fas fa-times"></i>
                                   </button>
                                 </div>
                               )}
                             </div>
                             {showDropdownAluno && todosAlunosOriginal && todosAlunosOriginal.length > 0 && (
                               <div
                                 style={{
                                   position: 'absolute',
                                   top: '100%',
                                   left: 0,
                                   right: 0,
                                   backgroundColor: 'white',
                                   border: '1px solid #ced4da',
                                   borderTop: 'none',
                                   borderRadius: '0 0 0.25rem 0.25rem',
                                   maxHeight: '300px',
                                   overflowY: 'auto',
                                   zIndex: 9999,
                                   boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                   marginTop: '-1px'
                                 }}
                               >
                                 {todosAlunosOriginal
                                   .filter(aluno => 
                                     aluno.name.toLowerCase().includes(buscaAluno.toLowerCase()) ||
                                     buscaAluno === ''
                                   )
                                   .map((aluno, index) => (
                                     <div
                                       key={index}
                                       onClick={() => {
                                         setBuscaAluno(aluno.name);
                                         setShowDropdownAluno(false);
                                       }}
                                       style={{
                                         padding: '8px 12px',
                                         cursor: 'pointer',
                                         backgroundColor: 'white',
                                         color: 'black',
                                         borderBottom: index < todosAlunosOriginal.filter(a => 
                                           a.name.toLowerCase().includes(buscaAluno.toLowerCase()) ||
                                           buscaAluno === ''
                                         ).length - 1 ? '1px solid #eee' : 'none'
                                       }}
                                       onMouseEnter={(e) => {
                                         e.target.style.backgroundColor = '#f8f9fa';
                                       }}
                                       onMouseLeave={(e) => {
                                         e.target.style.backgroundColor = 'white';
                                       }}
                                     >
                                       <i className="fas fa-user text-muted mr-2"></i>
                                       {aluno.name}
                                     </div>
                                   ))
                                 }
                                 {todosAlunosOriginal.filter(aluno => 
                                   aluno.name.toLowerCase().includes(buscaAluno.toLowerCase())
                                 ).length === 0 && buscaAluno && (
                                   <div style={{
                                     padding: '8px 12px',
                                     color: '#6c757d',
                                     fontStyle: 'italic'
                                   }}>
                                     <i className="fas fa-search text-muted mr-2"></i>
                                     Nenhum aluno encontrado
                                   </div>
                                 )}
                               </div>
                             )}
                           </div>
                         </div>
                        
                        {/* Filtro por Tema */}
                        <div className="col-xs-12 col-md-6 mb-3">
                          <div className="form-group dropdown-tema-container" style={{ position: 'relative' }}>
                            <label className="font-weight-bold text-dark">
                              <i className="fas fa-book text-primary mr-1"></i>
                              Filtrar por Tema
                            </label>
                            <div className="input-group">
                              <div className="input-group-prepend">
                                <span className="input-group-text">
                                  <i className="fas fa-filter"></i>
                                </span>
                              </div>
                              <input
                                type="text"
                                className="form-control form-control-lg"
                                placeholder="Digite o nome do tema..."
                                value={buscaTema}
                                onChange={(e) => {
                                  setBuscaTema(e.target.value);
                                  setShowDropdownTema(true);
                                }}
                                onFocus={() => setShowDropdownTema(true)}
                                 style={{ backgroundColor: 'white' }}
                               />
                               {buscaTema && (
                                 <div className="input-group-append">
                                   <button
                                     className="btn btn-outline-secondary"
                                     type="button"
                                     onClick={() => {
                                       setBuscaTema("");
                                       setIdTema(0);
                                       setShowDropdownTema(false);
                                     }}
                                     title="Limpar busca"
                                   >
                                     <i className="fas fa-times"></i>
                                   </button>
                                 </div>
                               )}
                            </div>
                            {showDropdownTema && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  backgroundColor: 'white',
                                  border: '1px solid #ced4da',
                                  borderTop: 'none',
                                  borderRadius: '0 0 0.25rem 0.25rem',
                                  maxHeight: '400px',
                                  overflowY: 'auto',
                                  zIndex: 9999,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  marginTop: '-1px'
                                }}
                              >
                                {(!buscaTema || "Todos os temas".toLowerCase().includes(buscaTema.toLowerCase())) && (
                                  <div
                                    onClick={() => {
                                      setIdTema(0);
                                      setBuscaTema("");
                                      setShowDropdownTema(false);
                                    }}
                                    style={{
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      backgroundColor: 'white',
                                      color: 'black'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#f8f9fa';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'white';
                                    }}
                                  >
                                    <i className="fas fa-list text-muted mr-2"></i>
                                    Todos os temas
                                  </div>
                                )}
                                {temas
                                  .filter(tema => 
                                    tema.nome.toLowerCase().includes(buscaTema.toLowerCase()) ||
                                    buscaTema === ''
                                  )
                                  .map((tema, index) => (
                                    <div
                                      key={index}
                                      onClick={() => {
                                        setIdTema(tema.id);
                                        setBuscaTema(tema.nome);
                                        setShowDropdownTema(false);
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: 'white',
                                        color: 'black',
                                        borderBottom: index < temas.filter(t => 
                                          t.nome.toLowerCase().includes(buscaTema.toLowerCase()) ||
                                          buscaTema === ''
                                        ).length - 1 ? '1px solid #eee' : 'none'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#f8f9fa';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'white';
                                      }}
                                    >
                                      <i className="fas fa-book text-muted mr-2"></i>
                                      {tema.nome}
                                    </div>
                                  ))
                                }
                                {temas.filter(tema => 
                                  tema.nome.toLowerCase().includes(buscaTema.toLowerCase())
                                ).length === 0 && buscaTema && (
                                  <div style={{
                                    padding: '8px 12px',
                                    color: '#6c757d',
                                    fontStyle: 'italic'
                                  }}>
                                    <i className="fas fa-search text-muted mr-2"></i>
                                    Nenhum tema encontrado
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-md-end mt-2 mt-md-0">
                        <small className="text-muted">
                          {buscaAluno ? (
                            <>
                              <i className="fas fa-filter me-1"></i>
                              Filtrado: {totalAlunos} resultado(s) para "{buscaAluno}"
                            </>
                          ) : (
                            <>
                              <i className="fas fa-users me-1"></i>
                              Total: {totalAlunos} aluno(s)
                            </>
                          )}
                        </small>
                  </div>

                  {/* Layout de Cards Moderno */}
                  <div className="row g-3">
                    {alunosTurma && alunosTurma.map((aluno) => (
                      <div key={aluno.id} className="col-12">
                        <div className="card shadow-sm border-0 mb-3">
                          <div className="card-header bg-primary d-flex justify-content-between align-items-center">
                             <div>
                               <h5 className="card-title mb-0 text-white">
                                 <i className="fas fa-user" style={{marginRight: '10px'}}></i>
                                 {aluno.name}
                               </h5>
                             </div>
                            {user.nivel === "a" && (
                               <button
                                 className="btn btn-outline-light btn-sm"
                                 onClick={() => setAlunoParaRemover(aluno)}
                                 title="Remover aluno da turma"
                               >
                                 <i className="fas fa-trash"></i> Remover
                               </button>
                             )}
                          </div>
                          <div className="card-body">
                            {atividades && atividades.length > 0 ? (
                              <div className="row g-1">
                                {atividades.map((atividade) => (
                                  <div key={atividade.tema_id} className="col-md-4 col-lg-3">
                                    <div className="border rounded p-2 bg-light" style={{minHeight: '120px'}}>
                                      <div className="mb-1">
                                        <small className="text-muted fw-bold d-block" style={{fontSize: '0.7rem'}}>
                                          {atividade.genero} {atividade.aula}
                                        </small>
                                        <h6 className="mb-1" title={atividade.nome} style={{fontSize: '0.8rem', lineHeight: '1.2'}}>
                                          {atividade.nome}
                                        </h6>
                                        <small className="text-muted d-block" style={{ fontSize: "0.65rem" }}>
                                          {tipoTurma === "UERJ" ? "Máx: 20pts" : "Máx: 1000pts"}
                                        </small>
                                      </div>
                                      <div className="mt-2">
                                        <MostrarNota
                                          key={`${aluno.id}-${atividade.tema_id}-${id_turma}`}
                                          alunoId={aluno.id}
                                          atividadeId={atividade.tema_id}
                                          turmaId={id_turma}
                                          tipoTurma={tipoTurma}
                                          notaPreCarregada={getNotaPreCarregada(aluno.name, atividade.nome)}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center text-muted py-4">
                                <i className="fas fa-info-circle mr-2"></i>
                                Nenhuma atividade encontrada para este aluno.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Mensagem quando não há alunos */}
                  {(!alunosTurma || alunosTurma.length === 0) && !loading && (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="fas fa-users fa-3x text-muted"></i>
                      </div>
                      <h5 className="text-muted">Nenhum aluno encontrado</h5>
                      <p className="text-muted">Adicione alunos à turma para começar.</p>
                    </div>
                  )}
                  
                  {/* Controles de Paginação de Alunos - Final da Página */}
                  {totalAlunos > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-4 p-3 bg-light rounded">
                       <div>
                         <strong className="text-dark">Página {alunosOffset} de {alunosPaginas}</strong>
                         <small className="text-dark d-block">Mostrando {alunosPorPagina} alunos por página</small>
                       </div>
                      <div>
                        <button
                           className="btn btn-outline-dark btn-sm mr-2"
                           onClick={() => setAlunosOffset(alunosOffset <= 1 ? 1 : alunosOffset - 1)}
                           disabled={alunosOffset <= 1}
                         >
                           ← Anterior
                         </button>
                         <span className="mx-2 text-dark">{alunosOffset}</span>
                         <button
                           className="btn btn-outline-dark btn-sm"
                           onClick={() => setAlunosOffset(alunosOffset >= alunosPaginas ? alunosPaginas : alunosOffset + 1)}
                           disabled={alunosOffset >= alunosPaginas}
                         >
                           Próxima →
                         </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </>
        )}
      </Row>

      <Modal
        show={!!alunoParaRemover}
        onHide={() => !removendo && setAlunoParaRemover(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 700, color: '#a12727' }}>
            <i className="fas fa-triangle-exclamation mr-2"></i>
            Remover aluno da turma
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2" style={{ fontSize: '14.5px', color: '#3d4d5c' }}>
            Remover <strong>{alunoParaRemover?.name}</strong> da turma{" "}
            <strong>{turma}</strong>?
          </p>
          <p className="mb-0" style={{ fontSize: '13px', color: '#8698ab' }}>
            O aluno deixa de aparecer nesta turma. A conta e as redações dele não são
            apagadas — ele pode ser adicionado a uma turma de novo depois.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            disabled={removendo}
            onClick={() => setAlunoParaRemover(null)}
          >
            Cancelar
          </Button>
          <Button variant="danger" disabled={removendo} onClick={confirmarRemocao}>
            {removendo ? "Removendo..." : "Remover da turma"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ListaAlunos;



// perfeito, mas faça o seguinte, transforme os temas em colunas e remova a coluna "tipo de atividade"